import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { catchError, exhaustMap, filter, mergeMap, share, switchMap, withLatestFrom } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { ContactGroupsActions } from '@states/contact-groups/contact-groups.actions';
import { contactGroupsFeature } from '@states/contact-groups/contact-groups.feature';
import { ContactGroupsService } from '../../services/contact-groups.service';
import { UtilsService } from '../../edge/utils.service';
import { SharedActions } from '@states/shared/shared.action-types';

const toSharedError = (utils: UtilsService, response: HttpErrorResponse | Error) =>
  SharedActions.showMessage({ error: utils.errMessage(response) });

export const startLoadingContactGroups$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(ContactGroupsActions.startLoadingContactGroups),
      withLatestFrom(
        store.select(contactGroupsFeature.selectListLoaded),
        store.select(contactGroupsFeature.selectIsLoading),
      ),
      filter(([, listLoaded, isLoading]) => !listLoaded && !isLoading),
      exhaustMap(() => [
        ContactGroupsActions.setIsLoading({ isLoading: true }),
        ContactGroupsActions.getContactGroups(),
      ]),
    ),
  { functional: true },
);

export const getContactGroups$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(ContactGroupsService),
    utils = inject(UtilsService),
  ) =>
    actions$.pipe(
      ofType(ContactGroupsActions.getContactGroups),
      switchMap(() =>
        service.getContactGroups().pipe(
          switchMap((contactGroups) => [
            ContactGroupsActions.getContactGroupsSuccess({ contactGroups }),
            ContactGroupsActions.setIsLoading({ isLoading: false }),
          ]),
          catchError((response) => [
            ContactGroupsActions.getContactGroupsFail(),
            ContactGroupsActions.setIsLoading({ isLoading: false }),
            toSharedError(utils, response),
          ]),
        ),
      ),
    ),
  { functional: true },
);

export const loadContactGroupMembers$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(ContactGroupsService),
    utils = inject(UtilsService),
  ) =>
    actions$.pipe(
      ofType(ContactGroupsActions.loadContactGroupMembers),
      switchMap(({ groupId }) =>
        service.getMembers(groupId).pipe(
          switchMap((res) => [
            ContactGroupsActions.loadContactGroupMembersSuccess({
              groupId: res.groupId,
              members: res.members,
            }),
          ]),
          catchError((response) => [
            ContactGroupsActions.loadContactGroupMembersFail({ groupId }),
            toSharedError(utils, response),
          ]),
        ),
      ),
    ),
  { functional: true },
);

export const startLoadingContactGroupMembers$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ContactGroupsActions.startLoadingContactGroupMembers),
      exhaustMap(({ groupId }) => [
        ContactGroupsActions.loadContactGroupMembers({ groupId }),
      ]),
    ),
  { functional: true },
);

export const startCreateContactGroup$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ContactGroupsActions.startCreateContactGroup),
      exhaustMap(({ create }) => [
        ContactGroupsActions.setIsSaving({ isSaving: true }),
        ContactGroupsActions.createContactGroup({ create }),
      ]),
      share(),
    ),
  { functional: true },
);

export const createContactGroup$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(ContactGroupsService),
    translate = inject(TranslateService),
    utils = inject(UtilsService),
  ) =>
    actions$.pipe(
      ofType(ContactGroupsActions.createContactGroup),
      mergeMap(({ create }) =>
        service.create(create).pipe(
          switchMap((contactGroup) => [
            ContactGroupsActions.createContactGroupSuccess({ contactGroup }),
            ContactGroupsActions.setIsSaving({ isSaving: false }),
            SharedActions.showMessage({
              success: translate.instant('TOASTS.CONTACT_GROUP_CREATED'),
            }),
          ]),
          catchError((response) => [
            toSharedError(utils, response),
            ContactGroupsActions.setIsSaving({ isSaving: false }),
            ContactGroupsActions.createContactGroupFail(),
          ]),
        ),
      ),
      share(),
    ),
  { functional: true },
);

export const startUpdateContactGroup$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ContactGroupsActions.startUpdateContactGroup),
      exhaustMap(({ id, update }) => [
        ContactGroupsActions.addToProcessingID({ id }),
        ContactGroupsActions.setIsSaving({ isSaving: true }),
        ContactGroupsActions.updateContactGroup({ id, update }),
      ]),
      share(),
    ),
  { functional: true },
);

export const updateContactGroup$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(ContactGroupsService),
    translate = inject(TranslateService),
    utils = inject(UtilsService),
  ) =>
    actions$.pipe(
      ofType(ContactGroupsActions.updateContactGroup),
      mergeMap(({ id, update }) =>
        service.update(id, update).pipe(
          switchMap((contactGroup) => [
            ContactGroupsActions.updateContactGroupSuccess({
              contactGroup,
            }),
            ContactGroupsActions.removeFromProcessingID({ id }),
            ContactGroupsActions.setIsSaving({ isSaving: false }),
            SharedActions.showMessage({
              success: translate.instant('TOASTS.CONTACT_GROUP_UPDATED'),
            }),
          ]),
          catchError((response) => [
            toSharedError(utils, response),
            ContactGroupsActions.removeFromProcessingID({ id }),
            ContactGroupsActions.setIsSaving({ isSaving: false }),
            ContactGroupsActions.updateContactGroupFail(),
          ]),
        ),
      ),
      share(),
    ),
  { functional: true },
);

export const startUpdateContactGroupMembers$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ContactGroupsActions.startUpdateContactGroupMembers),
      exhaustMap(({ id, contactIds, orgUserIds }) => [
        ContactGroupsActions.addToProcessingID({ id }),
        ContactGroupsActions.setIsSaving({ isSaving: true }),
        ContactGroupsActions.updateContactGroupMembers({
          id,
          contactIds,
          orgUserIds,
        }),
      ]),
      share(),
    ),
  { functional: true },
);

export const updateContactGroupMembers$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(ContactGroupsService),
    translate = inject(TranslateService),
    utils = inject(UtilsService),
  ) =>
    actions$.pipe(
      ofType(ContactGroupsActions.updateContactGroupMembers),
      mergeMap(({ id, contactIds, orgUserIds }) =>
        service.updateMembers(id, contactIds, orgUserIds).pipe(
          switchMap((contactGroup) => [
            ContactGroupsActions.updateContactGroupMembersSuccess({
              contactGroup,
            }),
            ContactGroupsActions.removeFromProcessingID({ id }),
            ContactGroupsActions.setIsSaving({ isSaving: false }),
            SharedActions.showMessage({
              success: translate.instant('TOASTS.CONTACT_GROUP_MEMBERS_UPDATED'),
            }),
            ContactGroupsActions.startLoadingContactGroupMembers({ groupId: id }),
          ]),
          catchError((response) => [
            toSharedError(utils, response),
            ContactGroupsActions.removeFromProcessingID({ id }),
            ContactGroupsActions.setIsSaving({ isSaving: false }),
            ContactGroupsActions.updateContactGroupMembersFail(),
          ]),
        ),
      ),
      share(),
    ),
  { functional: true },
);

export const startRemoveContactGroupMember$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ContactGroupsActions.startRemoveContactGroupMember),
      exhaustMap(({ groupId, memberId, memberType }) => [
        ContactGroupsActions.addToProcessingID({ id: groupId }),
        ContactGroupsActions.setIsSaving({ isSaving: true }),
        ContactGroupsActions.removeContactGroupMember({
          groupId,
          memberId,
          memberType,
        }),
      ]),
      share(),
    ),
  { functional: true },
);

export const removeContactGroupMember$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(ContactGroupsService),
    translate = inject(TranslateService),
    utils = inject(UtilsService),
  ) =>
    actions$.pipe(
      ofType(ContactGroupsActions.removeContactGroupMember),
      mergeMap(({ groupId, memberId, memberType }) =>
        service.removeMember(groupId, memberId, memberType).pipe(
          switchMap((contactGroup) => [
            ContactGroupsActions.removeContactGroupMemberSuccess({
              contactGroup,
            }),
            ContactGroupsActions.removeFromProcessingID({ id: groupId }),
            ContactGroupsActions.setIsSaving({ isSaving: false }),
            SharedActions.showMessage({
              success: translate.instant(
                'TOASTS.CONTACT_GROUP_MEMBER_REMOVED',
              ),
            }),
            ContactGroupsActions.startLoadingContactGroupMembers({ groupId }),
          ]),
          catchError((response) => [
            toSharedError(utils, response),
            ContactGroupsActions.removeFromProcessingID({ id: groupId }),
            ContactGroupsActions.setIsSaving({ isSaving: false }),
            ContactGroupsActions.removeContactGroupMemberFail(),
          ]),
        ),
      ),
      share(),
    ),
  { functional: true },
);

export const startDeleteContactGroup$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ContactGroupsActions.startDeleteContactGroup),
      exhaustMap(({ id }) => [
        ContactGroupsActions.addToProcessingID({ id }),
        ContactGroupsActions.deleteContactGroup({ id }),
      ]),
      share(),
    ),
  { functional: true },
);

export const deleteContactGroup$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(ContactGroupsService),
    translate = inject(TranslateService),
    utils = inject(UtilsService),
  ) =>
    actions$.pipe(
      ofType(ContactGroupsActions.deleteContactGroup),
      mergeMap(({ id }) =>
        service.delete(id).pipe(
          switchMap(() => [
            ContactGroupsActions.deleteContactGroupSuccess({ id }),
            ContactGroupsActions.removeFromProcessingID({ id }),
            SharedActions.showMessage({
              success: translate.instant('TOASTS.CONTACT_GROUP_DELETED'),
            }),
          ]),
          catchError((response) => [
            toSharedError(utils, response),
            ContactGroupsActions.removeFromProcessingID({ id }),
            ContactGroupsActions.deleteContactGroupFail(),
          ]),
        ),
      ),
      share(),
    ),
  { functional: true },
);
